import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import ErrorRetry from "@/components/ErrorRetry";
import { usePolling } from "@/lib/usePolling";
import { THREAD_POLL_MS } from "@/lib/polling";
import { Send, Loader2, MessageSquare, Check, CheckCheck } from "lucide-react";

export default function PatientMessages() {
  const [messages, setMessages] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [sendErr, setSendErr] = useState(null);
  const endRef = useRef(null);

  /** `markRead` is false on a poll: the clinic reads read_by_patient to know whether
   *  somebody actually saw what they were told, and a tab sitting open must not answer
   *  that question on the patient's behalf. Only a deliberate open or a reply marks read. */
  const load = useCallback(async ({ scroll = true, markRead = true } = {}) => {
    try {
      const { data } = await api.get("/patient/messages", { params: { mark_read: markRead } });
      setMessages(data.messages);
      setLoadErr(null);
      if (scroll) {
        setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      }
    } catch (e) {
      // Leaves `messages` as null on a first failure, so the render below shows the
      // error instead of an empty thread. An empty thread would tell a patient their
      // care team had said nothing to them.
      setLoadErr(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The patient portal has one thread, so there is no list to poll — just the
  // conversation, on the same short cycle as the staff side. Paused while the tab is
  // hidden; does not touch the idle-logoff timer (lib/polling.js).
  const poll = useCallback(() => load({ scroll: false, markRead: false }), [load]);
  usePolling(poll, THREAD_POLL_MS);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    setSendErr(null);
    try {
      await api.post("/patient/messages", { body });
      setBody("");            // only after it actually sent
      await load();
    } catch (e) {
      // Was `catch (err) {}`. The draft was cleared either way, so a failed message to
      // a care team looked exactly like a sent one — the worst possible silent failure
      // on this screen.
      setSendErr(e);
    }
    setSending(false);
  };

  return (
    <div className="space-y-6" data-testid="patient-messages">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Messages</h1>
        <p className="text-sm text-muted-foreground mt-1">Securely message your care team.</p>
      </div>

      <div className="bg-card border border-border rounded-xl flex flex-col h-[520px]">
        <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="patient-thread">
          {messages === null && loadErr ? (
            /* Was "Loading…" forever. A patient staring at a spinner concludes the clinic
               has gone quiet, which is the opposite of what this screen is for. */
            <div className="h-full flex items-center justify-center">
              <ErrorRetry
                error={loadErr}
                onRetry={() => { setLoadErr(null); load(); }}
                title="Your messages couldn't be loaded"
                note="This isn't your conversation — it's a connection problem. Nothing has been lost."
                testid="patient-messages-error"
              />
            </div>
          ) : messages === null ? <p className="text-sm text-muted-foreground">Loading…</p>
            : messages.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground text-center">
                <div><MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />Start a conversation with your care team.</div>
              </div>
            ) : messages.map((m) => {
              const mine = m.sender_role === "patient";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                    <p className="text-sm">{m.body}</p>
                    <p className={`text-[10px] mt-1 inline-flex items-center gap-1 ${mine ? "text-white/60" : "text-muted-foreground"}`}>
                      {mine ? "You" : m.sender_name} · {new Date(m.created_at).toLocaleString()}
                      {/* Whether it was READ. Reading is not replying — the clinic still
                          owes an answer, and their worklist still says so. */}
                      {mine
                        ? <span data-testid={`msg-status-${m.id}`} title={m.read_by_staff ? "Seen by the clinic" : "Sent"}>
                            {m.read_by_staff ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                          </span>
                        : (m.read_by_patient ? null : (
                            <span data-testid={`msg-new-${m.id}`} className="px-1 rounded bg-primary/10 text-primary">new</span>
                          ))}
                    </p>
                  </div>
                </div>
              );
            })}
          <div ref={endRef} />
        </div>
        <div className="px-3 pt-3 space-y-2">
          <ErrorRetry
            error={messages === null ? null : loadErr}
            variant="inline"
            onRetry={() => { setLoadErr(null); load(); }}
            note="This conversation may be out of date."
            testid="patient-messages-stale"
          />
          <ErrorRetry
            error={sendErr}
            variant="inline"
            onRetry={() => setSendErr(null)}
            note="Your message was not sent. The text is still in the box — try again."
            testid="patient-send-error"
          />
        </div>
        <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
          <input data-testid="patient-message-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…" className="flex-1 rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
          <button data-testid="patient-send-message" disabled={sending || !body.trim()} className="bg-primary text-primary-foreground rounded-md px-4 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center gap-1.5">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
}
